import { nonnull } from "@tento-core/assert";
import {
  AuthId,
  CalendarId,
  EventId,
  EventItemAllDay,
  EventItemTimed,
  Json,
  type JsonObject,
  type Option,
  type RowLocalId,
} from "@tento-lona/sheets";
import { CalendarColumnCell } from "@tento-ui/components/calendar/calendar-column-cell";
import { EntryList } from "@tento-ui/components/entry-list/entry-list";
import type {
  EntryDisplay,
  ListItem,
} from "@tento-ui/components/entry-list/list-item";
import { CalendarSubcolumn } from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import type { ItemActionContext } from "@tento-lona/sheets-ui/sheet/gestures/interactable-item.ts";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import {
  type BindCellContext,
  type RenderPlugin,
  RowOptionsElement,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import {
  SheetViewModel,
  type SheetRow,
  AuthScopedCalendarId,
  CalendarsClient,
  EventCategory,
  CalendarPreferences,
} from "@tento-lona";
import { calendarEventRead, type CalendarEventRead, type CalendarListConfig } from "@tento-lona/platform";
import { SpanReads } from "./calendar-span-reads.ts";
import type { CalendarReadValue } from "@tento-lona/sheets-ui/sheet/calendar-read-revision.ts";
import { ComponentIcon, LonaIcons } from "@tento-ui/ui/icons";
import {
  DateTime,
  DateUnit,
  NaiveDate,
  type TimezoneRegion,
  Utc,
} from "@tento-chrono";
import { AllDayEventsProvider } from "@tento-lona/sheets-ui/plugins/renderer/event/build-all-day-events-provider.ts";
import { EventListItems } from "@tento-lona/sheets-ui/plugins/renderer/event/build-event-list-rows.ts";
import {
  eventSubcolumnProvider,
  type SubcolumnProvider,
} from "@tento-lona/sheets-ui/plugins/renderer/event/event-subcolumn-provider.ts";
import type { EventSubcolumnHost } from "@tento-lona/sheets-ui/plugins/renderer/event/build-event-subcolumn.ts";
import {
  eventCalendarMonthSubcolumnProvider,
  type CalendarMonthSubcolumnProvider,
} from "@tento-lona/sheets-ui/plugins/renderer/event/event-calendar-month-subcolumn-provider.ts";
import type { EventCalendarMonthHost } from "@tento-lona/sheets-ui/plugins/renderer/event/build-event-calendar-month-subcolumn.ts";
import type { CalendarMonthActions } from "@tento-lona/sheets-ui/plugins/renderer/event/calendar-month-actions.ts";
import type { CalendarMonthSubcolumn } from "@tento-lona/sheets-ui/sheet/calendar-month-subcolumn.ts";
import type { EventItemPresentation, EventsPlatform } from "@tento-lona";
import { EventItemDelegate, readOnlyEventItemDelegate } from "@tento-lona/sheets-ui/sheet/gestures/event-gesture-item.ts";
import type { EventWriter } from "@tento-lona/sheets-ui/plugins/renderer/event/events-source.ts";
import { EventAccess } from "@tento-lona/sheets-ui/sheet/gestures/event-access.ts";
import type { ExistingEventDetails } from "@tento-lona/sheets-ui/plugins/renderer/column-calendar/components/existing-event-details.ts";
import type {
  TimelineContentProvider,
  TimelinePlugin,
} from "@tento-lona/sheets-ui/plugins/layout/timeline-shared/timeline-content-provider.ts";
import type { ContentProviders } from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { CalendarListEventsProvider } from "@tento-lona/sheets/platform";
import type { EventsCacheSlots } from "@tento-lona/sheets/platform";
import { CALENDAR_LIST_ALIAS_SPEC } from "@tento-lona/sheets/platform";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";
import { EventListModal } from "@tento-lona/sheets-ui/plugins/renderer/column-calendar/components/event-list-modal.ts";
import { ListCell } from "@tento-lona/sheets-ui/plugins/renderer/list-cell.ts";
import { EventActiveState } from "@tento-lona/sheets-ui/plugins/renderer/event/event-active-state.ts";
import type { SheetBinder } from "../../sheet/sheet-index-renderer.ts";

/**
 * The calendar list's alias plugin: a calendar-list row aliases the user's
 * calendars into the sheet and supplies their events to the day, month and
 * all-day surfaces (`contentProviders`), with the write access its list
 * config grants (`subcolumnAccess`).
 */
export class CalendarListAliasPlugin
  implements
    RenderPlugin<HTMLElement, SheetLegend.DefaultRow>,
    RenderPlugin.Alias<HTMLElement, SheetLegend.DefaultRow>
{
  static readonly spec: PerTypeSpec = CALENDAR_LIST_ALIAS_SPEC;

  readonly id: string = CALENDAR_LIST_ALIAS_SPEC.id;
  readonly role = "alias";
  readonly icon: HTMLTemplateElement = LonaIcons.$template(ComponentIcon);

  /**
   * Pure data half of this plugin — exposed so CLI inspection and the
   * shared catalog `dataProvider` factory get the same row-walking
   * logic that DOM uses internally for fixed-child / subcolumn output.
   */
  readonly dataProvider: CalendarListEventsProvider =
    new CalendarListEventsProvider();

  /**
   * Resolve filter config from the calendar-list row's attribute
   * bag. `calendar-list` is an alias plugin, so its chrome/picker
   * config lives on the alias row while event data flows through
   * the nested `list` aggregate facade.
   */
  /**
   * The row's calendar-list membership (F5): which items it shows, by the
   * SDK's row semantics (`CalendarEventRead.membership`, characterized against
   * this file's former decode + filter code), with the user-level calendar
   * visibility injected.
   */
  #membership(row: SheetRow.Joined.Handle): (item: EventItemTimed | EventItemAllDay) => boolean {
    return this.#eventRead(row).membership((acid) => this.callbacks.isCalendarVisible(acid));
  }

  constructor(
    public readonly calendars: CalendarsClient,
    public readonly makeRowOptions: RowOptionsElement.Factory,
    public readonly callbacks: CalendarListAliasPlugin.Callbacks,
    public readonly calendarPreferences: Option<CalendarPreferences>,
    public readonly eventItemDelegate: Option<EventItemDelegate>,
    public readonly eventActiveState: EventActiveState,
    public readonly makeActions: Option<CalendarMonthActions.Factory>,
    /**
     * The narrow write seam for month-view event mutations (writes
     * resolve the source row from `event.origin` internally). `null` in
     * contexts that don't write (e.g. null-state preview). Supplied by
     * the SDK-aware host (plugin-manager) as `sdk.events`, which
     * structurally satisfies `EventWriter` and the details panel's
     * `ExistingEventDetails.Writer` (the overflow list's details write
     * through it) — the plugin depends only on those narrow surfaces, not
     * the whole SDK client.
     */
    public readonly events: Option<EventWriter & ExistingEventDetails.Writer>,
    /**
     * The client's event categories and drafts (`sdk.events`): every event
     * this plugin paints is titled and coloured through it — present in
     * every context, including one that does not write.
     */
    public readonly presentation: EventItemPresentation.Scope,
  ) {}

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return "Display a list of calendar events from all your connected calendars. Filter by calendar visibility and time ranges.";
  }

  onResize(): void {}
  loadData = null;
  legendFactory = null;
  onBindRow(): void {}
  onRenderedColumnsWillChange(): void {}
  onViewportColumnsChanged(): void {}

  // Keep the SDK provider's per-instance indexes across queries for a handle.
  // Raw slots remain owned by the renderer's synchronous bind.
  readonly #eventReads = new WeakMap<SheetRow.Joined.Handle, CalendarEventRead>();

  #eventRead(row: SheetRow.Joined.Handle): CalendarEventRead {
    let read = this.#eventReads.get(row);
    if (read == null) {
      read = calendarEventRead(row);
      this.#eventReads.set(row, read);
    }
    return read;
  }

  #partialEventsInRange(
    row: SheetRow.Joined.Handle,
    range: DateTime.Range<Utc>,
    cache: Option<EventsCacheSlots>,
    bindRange: Option<DateTime.Range<Utc>>,
    zone: Option<TimezoneRegion>,
  ): readonly EventItemTimed[] {
    return this.#spanReads.timed(this.#eventRead(row), row, range, cache, bindRange, zone);
  }

  /** One SDK span read per row per strip bind, sliced per day (F2). */
  readonly #spanReads = new SpanReads<SheetRow.Joined.Handle>();

  #allDayEventsInRange(
    row: SheetRow.Joined.Handle,
    range: NaiveDate.Range,
  ): readonly EventItemAllDay[] {
    // No bind cache: the all-day row reads once per bind (timeline-row-event's
    // `items(range)` over the bind's whole range), so a per-bind cache would
    // be built and dropped unused; the strip's cache is a different bind's.
    return this.#eventRead(row).allDay(range, null);
  }

  /**
   * SubcolumnProvider registry — today: one provider (events).
   * Adding a per-shape renderer (e.g., tasks) registers a sibling
   * provider here and the aggregator dispatches by first-match
   * automatically.
   */
  readonly #subcolumnProviders: readonly SubcolumnProvider<
    EventSubcolumnHost
  >[] = [eventSubcolumnProvider];

  /**
   * CalendarMonthSubcolumnProvider registry — today: one provider
   * (events). Mirrors `subcolumnProviders` for the grid (calendar-month)
   * layout; a future grid task/weather renderer registers a sibling and
   * the aggregator dispatches by first-match automatically.
   */
  readonly #calendarMonthSubcolumnProviders: readonly CalendarMonthSubcolumnProvider<
    EventCalendarMonthHost
  >[] = [eventCalendarMonthSubcolumnProvider];

  /**
   * Per-row actions cache for the calendar-month provider, keyed by the
   * alias row id so `CalendarMonthActions` + its gestures are built once
   * per row — re-binding reuses them instead of stacking gestures on the
   * layout's gesture manager (mirrors grid-event's `actionsByRowId`).
   */
  readonly #calendarMonthActionsByRowId = new Map<
    RowLocalId,
    CalendarMonthActions
  >();

  #calendarProvider: CalendarSubcolumn.Provider =
    ((self) => ({
    subcolumnFromRow(
      _sheet: SheetViewModel.Surface<SheetBinder>,
      row: SheetRow.Joined.Handle,
      _parentRow: SheetRow.Joined.Handle,
    ): CalendarSubcolumn {
      // Iterate registered shape→subcolumn providers; first match
      // wins. The aggregator no longer encodes which renderer
      // handles which shape — providers self-register via the
      // `subcolumnProviders` field.
      const host = self.#eventSubcolumnHostFor(row);
      for (const provider of self.#subcolumnProviders) {
        const subcolumn = provider.subcolumnFor(row, host);
        if (subcolumn) return subcolumn;
      }
      // No provider matched — return an empty event subcolumn so
      // the timeline still gets a renderer. (Future: an empty
      // any-shape subcolumn factory.)
      return nonnull(
        eventSubcolumnProvider.subcolumnFor(row, host),
        "the event subcolumn provider answers every calendar row",
      );
    },
  }))(this);

  /**
   * Calendar-month (grid) content provider. The month layout discovers
   * this on the calendar-list alias and asks for a subcolumn; we build a
   * host (layout context + actions factory + the row's SDK month read and
   * membership) and dispatch to the registered providers, first match
   * wins. The subcolumn reads events across every connected calendar via
   * `AggregatingEventsSource`, fixing the prior per-source behavior that
   * showed only the first calendar's events.
   */
  #calendarMonthProvider: CalendarMonthSubcolumn.Provider =
    ((self) => ({
      subcolumnFromRow(
        _sheet: Option<SheetViewModel.Surface<SheetBinder>>,
        row: SheetRow.Joined.Handle,
        _parentRow: SheetRow.Joined.Handle,
        layoutHost: CalendarMonthSubcolumn.Host,
      ): Option<CalendarMonthSubcolumn> {
        const makeActions = self.makeActions;
        const events = self.events;
        // No actions factory / SDK events client (e.g. null-state
        // preview) — this context can't build a writable calendar-month
        // subcolumn.
        if (!makeActions || !events) return null;
        const eventsHost: EventCalendarMonthHost = {
          layout: layoutHost,
          makeActions,
          events,
          monthRead: self.#eventRead(row),
          // Read per call: the list's filter can change after this host is cached (N4-2).
          member: () => self.#membership(row),
          cachedActions: (id) =>
            self.#calendarMonthActionsByRowId.get(id) ?? null,
          storeActions: (id, actions) => {
            self.#calendarMonthActionsByRowId.set(id, actions);
          },
        };
        for (const provider of self.#calendarMonthSubcolumnProviders) {
          const subcolumn = provider.subcolumnFor(row, eventsHost);
          if (subcolumn) return subcolumn;
        }
        return null;
      },
    }))(this);

  /**
   * Build an `EventSubcolumnHost` for `row` with the row's
   * category/visibility config baked into `filterEvent`. Constructed
   * per-row at provider-dispatch time; cheap (closures + property
   * reads) and avoids the aggregator-side filter-config indirection
   * the previous two-tier host layered in.
   */
  /**
   * The write access a calendar list's event subcolumns get from its config
   * (`CalendarListConfig.access`, sdk-to-dom-12): a read-only list gets no
   * event delegate (no move/resize), no create gesture, and dims its events.
   */
  static subcolumnAccess(config: CalendarListConfig): CalendarListAliasPlugin.SubcolumnAccess {
    // Exhaustive over `access` and closed on an unknown state (N3-1).
    const list = EventAccess.fromListConfig(config.access);
    const editable = list === "editable";
    return { editable, canCreate: EventAccess.Create.offered(list), readOnlyOpacity: editable ? null : "0.3" };
  }

  #readIdentity(row: SheetRow.Joined.Handle, config: CalendarListConfig): readonly CalendarReadValue[] {
    return [
      this, this.callbacks, this.calendars, this.calendarPreferences,
      this.callbacks.newEventActions, this.callbacks.onShowFlightInfo,
      this.calendarPreferences?.peek().dateFormat,
      JSON.stringify(config),
      // F8: the read's own generation (sdk-to-dom-7 item 3): a completed
      // read is reused only while nothing it consulted changed.
      this.#eventRead(row).generation(),
      // A pre-load render keys on no calendars (the load re-renders).
      JSON.stringify((this.calendars.isLoaded() ? this.calendars.peekAll() : []).map(list => [
        list.authId, list.calendars.map(calendar => [
          calendar, this.callbacks.isCalendarVisible(AuthScopedCalendarId.of([list.authId, calendar])),
        ]),
      ])),
    ];
  }

  #eventSubcolumnHostFor(
    row: SheetRow.Joined.Handle,
  ): EventSubcolumnHost {
    const config = this.#eventRead(row).peekListConfig();
    const access = CalendarListAliasPlugin.subcolumnAccess(config);
    const member = this.#membership(row);
    return {
      readIdentity: () => this.#readIdentity(row, config),
      // A read-only list still opens details (read-only), but nothing moves,
      // resizes or offers the writing context menu (N4-1 day/month parity).
      baseDelegate: access.editable || this.eventItemDelegate == null
        ? this.eventItemDelegate
        : readOnlyEventItemDelegate(this.eventItemDelegate),
      eventActiveState: this.eventActiveState,
      presentation: this.presentation,
      canCreate: access.canCreate,
      readOnlyOpacity: access.readOnlyOpacity,
      partialEventsInRange: (r, range, cache, bindRange, zone) =>
        this.#partialEventsInRange(r, range, cache, bindRange, zone),
      prepareEventsInRange: (r, range) => this.#eventRead(r).prepareTimed(range),
      filterEvent: (event) => member(event),
      timeFormat: () => this.calendarPreferences?.peek().timeFormat ?? "12h",
      newEventActions: this.callbacks.newEventActions,
      onShowOccluded: ({ events, side, validHours, date, tz, row: itemRow }) => {
        const dateFormat =
          this.calendarPreferences?.peek().dateFormat ?? "month-day";
        const dateStr = NaiveDate.Formatter.render(date, {
          dateFormat,
          shortMonth: true,
        });
        const boundary = side === "above" ? validHours.start : validHours.end;
        const boundaryStr = boundary.format({
          includeSeconds: false,
          includeAmPm: true,
        });
        const description =
          side === "above"
            ? `Before ${boundaryStr} on ${dateStr}`
            : `After ${boundaryStr} on ${dateStr}`;
        EventListModal.make()
          .bind(
            events as EventItemTimed[],
            description,
            tz,
            this.calendars,
            itemRow,
            this.calendarPreferences?.peek().timeFormat ?? "12h",
            dateFormat,
            this.callbacks.onShowFlightInfo,
            this.eventActiveState,
            this.presentation,
            this.events,
            EventAccess.fromListConfig(this.#eventRead(row).peekListConfig().access),
          )
          .showAsModal();
      },
    };
  }

  contentProviders: ContentProviders = {
    calendar: this.#calendarProvider,
    calendarMonth: this.#calendarMonthProvider,
    timeline: {
      subcolumnFromRow: (row, _parentRow) => this.makeTimelineProvider(row),
    } satisfies TimelinePlugin.SubcolumnProvider<EventItemAllDay>,
  };

  /**
   * Create a timeline content provider for a specific row.
   * Returns full-day events filtered by the row's variant config.
   * Renderer-specific construction (cells, ghosts, overflow
   * indicator, tooltip) lives in the event renderer module.
   */
  makeTimelineProvider(
    row: SheetRow.Joined.Handle,
  ): TimelineContentProvider.Provider<EventItemAllDay> {
    const member = this.#membership(row);

    return AllDayEventsProvider.build({
      presentation: this.presentation,
      items: (range) =>
        this.#allDayEventsInRange(row, range).filter((e) => member(e)),
      ensureColumnsLoaded: async (columns) => {
        const [pd, tz] = columns.start;
        const start = tz.toWallClock(pd.start.withTime()).toUtc();
        const [endPd, endTz] = columns.end;
        const end = endTz.toWallClock(endPd.end.withTime()).toUtc();
        await row.events().ensureLoaded(
          new DateTime.Range(start, end),
          DateUnit.days(90),
        );
      },
      // Exhaustive over `access` and closed on an unknown state, as for the
      // event subcolumns (`subcolumnAccess`).
      calendarListAccess: () => EventAccess.fromListConfig(this.#eventRead(row).peekListConfig().access),
    });
  }

  makeCell(): EntryList {
    return ListCell.make();
  }

  async bindCell($cell: EntryList, ctx: BindCellContext) {
    $cell.assignStyles({
      "--entry-list-bg-color": "var(--bg-surface-light)",
    });
    $cell.bind({
      items: await this.toProps(ctx),
      display: this.display(ctx),
      onRowBound: (item, $row) => {
        const eventId = EventId.parse(item.id ?? "");
        if (eventId == null) return;
        this.eventActiveState.bind(eventId, $row);
      },
    });
  }

  /**
   * The event renderer's `toProps`: read the column's events (filtered by
   * category/visibility) and map each to the source-agnostic `ListItem`
   * viewmodel. Shared by this alias's own column cell and by
   * `ColumnEventListRendererPlugin` (which resolves the alias ancestor and delegates).
   */
  async toProps({ column, row }: BindCellContext): Promise<ListItem[]> {
    const pd = column.discrete()[0];
    const local = column.tz;
    const member = this.#membership(row);

    const start = local.toWallClock(pd.start.withTime()).toUtc();
    const end = local.toWallClock(pd.end.withTime()).toUtc();
    const utcRange = new DateTime.Range(start, end);
    const events = this.#partialEventsInRange(row, utcRange, null, null, local).filter((e) => member(e));

    return EventListItems.build({
      events,
      eventsPlatform: this.callbacks.eventsPlatform,
      tz: local,
      calendars: this.calendars,
      timeFormat: this.calendarPreferences?.peek().timeFormat ?? "12h",
      dateFormat: this.calendarPreferences?.peek().dateFormat ?? "month-day",
      presentation: this.presentation,
      list: EventAccess.fromListConfig(this.#eventRead(row).peekListConfig().access),
    });
  }

  /** List-level display context for the event list — honors the user's
   *  clock/date preferences. */
  display({ row }: BindCellContext): EntryDisplay {
    return {
      timeScale: row.timescaleSetting === "custom" ? "day" : row.timescaleSetting,
      use24Hour: this.calendarPreferences?.peek().timeFormat === "24h",
      dateFormat: this.calendarPreferences?.peek().dateFormat ?? "month-day",
    };
  }
}

/**
 * Types and values that go with `CalendarListAliasPlugin`: `Callbacks`,
 * `Attributes`, `decodeAttributes`.
 */
export namespace CalendarListAliasPlugin {
  /** What `subcolumnAccess` grants a list's event subcolumns. */
  export type SubcolumnAccess = {
    /** Whether events carry the move/resize delegate. */
    readonly editable: boolean;
    /** Whether the subcolumn offers the create gesture. */
    readonly canCreate: boolean;
    /** The events' opacity on a read-only list, else null. */
    readonly readOnlyOpacity: Option<string>;
  };

  export type Callbacks = {
    isCalendarVisible: (acid: AuthScopedCalendarId) => boolean;
    eventsPlatform: EventsPlatform;
    newEventActions: {
      show(
        timeRange: DateTime.Range<Utc>,
        context: ItemActionContext,
      ): void;
      showGhost(timeRange: DateTime.Range<Utc>): void;
      hide(): void;
      isRevealed(): boolean;
    };
    /** Hook invoked from `EventListModal` → `ExistingEventDetails`
     *  when the user asks to inspect a flight-typed event. */
    onShowFlightInfo: () => void;
  };

  /** A calendar-list alias's picker config as `decodeAttributes` reads it: `null` where the bag does not say. */
  export type Attributes = {
    readonly allSelected: Option<boolean>;
    readonly categories: Option<EventCategory[]>;
    readonly visibilityMode: Option<"default" | "custom">;
    readonly customCalendars: Option<AuthScopedCalendarId[]>;
    readonly readOnly: Option<boolean>;
  };

  const VISIBILITY_MODES: readonly ("default" | "custom")[] = ["default", "custom"];
  const CATEGORIES: readonly EventCategory[] = [
    "work",
    "social",
    "exercise",
    "transportation",
    "food",
    "shower",
    "errands",
    "nap",
    "creative",
    "break",
  ];

  /**
   * Read a calendar-list alias's picker config off its bag. Unknown
   * categories and malformed `[authId, calendarId]` pairs are dropped.
   */
  export function decodeAttributes(attributes: JsonObject): Attributes {
    const categories = Json.strings(attributes, "categories");
    const calendars = Json.array(attributes, "customCalendars");
    return {
      allSelected: Json.boolean(attributes, "allSelected"),
      categories: categories == null
        ? null
        : CATEGORIES.filter((c) => categories.includes(c)),
      visibilityMode: Json.oneOf(attributes, "visibilityMode", VISIBILITY_MODES),
      customCalendars: calendars == null ? null : decodeCalendarIds(calendars),
      readOnly: Json.boolean(attributes, "readOnly"),
    };
  }

  function decodeCalendarIds(list: readonly Json[]): AuthScopedCalendarId[] {
    const out: AuthScopedCalendarId[] = [];
    for (const item of list) {
      if (!Array.isArray(item) || item.length !== 2) continue;
      const [authId, calendarId] = item;
      if (typeof authId !== "string" || typeof calendarId !== "string") continue;
      const acid = AuthScopedCalendarId.parse(authId, calendarId);
      if (acid != null) out.push(acid);
    }
    return out;
  }
}
